import { useEffect, useMemo, useState } from "react";
import { Lock, ChevronDown, RefreshCw, Inbox, FileIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PageFooter } from "@/components/PageFooter";
import { inputCls } from "@/components/setup/SetupField";

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_CODE = "MFBadmin";

const AGENCY_LABELS = {
  master_fb: "Master F&B manages printing",
  own_agency: "Own local agency",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

const fmtSize = (bytes) => {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-[150px_1fr] gap-3 py-1.5 border-b border-[#f0f0f0] last:border-0">
    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999999] pt-0.5">{label}</span>
    <span className="text-sm text-[#000000] break-words">{value || "—"}</span>
  </div>
);

const peopleText = (people) =>
  (people || [])
    .map((p) => `${p.role ? `${p.role}: ` : ""}${p.name}${p.email ? ` — ${p.email}` : ""}`)
    .join(" · ");

const FileLinks = ({ files, testId }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {files.map((f) => (
      <a
        key={f.file_id}
        href={`${API}/api/uploads/${f.file_id}`}
        className="inline-flex items-center gap-2 border border-[#e8e8e8] rounded-sm px-3 py-2 text-xs text-[#000000] hover:border-[#dd260e] hover:text-[#dd260e] transition-colors"
        data-testid={testId}
      >
        <FileIcon className="w-3.5 h-3.5 shrink-0" />
        {f.filename} <span className="text-[#999999]">({fmtSize(f.size)})</span>
      </a>
    ))}
  </div>
);

const SubmissionDetail = ({ sub }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
    <div>
      <DetailRow label="Country" value={sub.country} />
      <DetailRow label="BU Owner" value={`${sub.bu_owner_name} — ${sub.bu_owner_email}`} />
      <DetailRow label="IT Liaison" value={sub.it_liaison_name ? `${sub.it_liaison_name} — ${sub.it_liaison_email}` : ""} />
      <DetailRow label="Local SPOC" value={sub.spoc_name ? `${sub.spoc_name} — ${sub.spoc_email}` : ""} />
      <DetailRow label="Default language" value={sub.default_language} />
      <DetailRow label="Add. languages" value={(sub.additional_languages || []).join(", ")} />
      <DetailRow label="Currency" value={sub.currency} />
      <DetailRow label="Brand color" value={sub.brand_color} />
      <DetailRow label="Brand notes" value={sub.brand_notes} />
    </div>
    <div>
      <DetailRow label="Segments" value={(sub.segments || []).join(", ")} />
      <DetailRow label="Print agency" value={AGENCY_LABELS[sub.print_agency] || sub.print_agency} />
      <DetailRow label="Print contact" value={sub.print_contact} />
      <DetailRow label="Go-live target" value={sub.golive_target} />
      <DetailRow label="Requirements" value={sub.additional_requirements} />
      <DetailRow label="Training" value={peopleText(sub.training_assignments)} />
      <DetailRow label="Touchpoints" value={peopleText(sub.main_touchpoints)} />
      <DetailRow label="Banner plan" value={sub.banner_plan} />
      <DetailRow label="Submission ID" value={sub.id} />
    </div>
    {(sub.asset_files || []).length > 0 && (
      <div className="lg:col-span-2 mt-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999999]">Uploaded assets</span>
        <FileLinks files={sub.asset_files} testId="submission-file-link" />
      </div>
    )}
  </div>
);

const DeliveryDetail = ({ d }) => (
  <div>
    <DetailRow label="Contact" value={`${d.contact_name} — ${d.contact_email}`} />
    <DetailRow label="Note" value={d.note} />
    <DetailRow label="Delivery ID" value={d.id} />
    {(d.asset_files || []).length > 0 && (
      <div className="mt-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#999999]">Files</span>
        <FileLinks files={d.asset_files} testId="delivery-file-link" />
      </div>
    )}
  </div>
);

const AdminGate = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);

  const tryUnlock = () => {
    if (code === ADMIN_CODE) {
      sessionStorage.setItem("fora_admin_auth", "1");
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div className="bg-white border border-[#e8e8e8] rounded-sm p-10 sm:p-14 max-w-md mx-auto mt-10 text-center" data-testid="submissions-admin-gate">
      <div className="w-12 h-12 rounded-sm bg-[#000000] flex items-center justify-center mx-auto mb-5">
        <Lock className="w-5 h-5 text-white" strokeWidth={1.8} />
      </div>
      <span className="cchbc-eyebrow justify-center">Master F&amp;B only</span>
      <h2 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] mt-3 mb-2">Admin Access</h2>
      <p className="text-xs text-[#555555] mb-6">Country setup submissions contain confidential market data. Enter the admin code to continue.</p>
      <input
        type="password"
        className={`${inputCls} text-center ${error ? "border-[#dd260e]" : ""}`}
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          setError(false);
        }}
        onKeyDown={(e) => e.key === "Enter" && tryUnlock()}
        placeholder="Admin code"
        data-testid="submissions-admin-code-input"
      />
      {error && (
        <p className="text-[11px] text-[#dd260e] font-semibold mt-2" data-testid="submissions-admin-error">
          Incorrect code
        </p>
      )}
      <button onClick={tryUnlock} className="cchbc-btn cchbc-btn-primary w-full justify-center mt-4" data-testid="submissions-admin-unlock-btn">
        Unlock
      </button>
    </div>
  );
};

const SubmissionRow = ({ sub, expanded, onToggle, index }) => (
  <div className="border-b border-[#e8e8e8] last:border-0" data-testid={`submission-row-${index}`}>
    <button
      onClick={onToggle}
      className="w-full grid grid-cols-[1fr_auto] sm:grid-cols-[160px_1fr_1fr_120px_24px] gap-3 px-5 py-4 text-left hover:bg-[#fafafa] transition-colors items-center"
      data-testid={`submission-toggle-${index}`}
    >
      <span className="text-xs text-[#555555]">{fmtDate(sub.created_at)}</span>
      <span className="text-sm font-bold text-[#000000] hidden sm:block">{sub.country}</span>
      <span className="text-xs text-[#555555] hidden sm:block truncate">
        {sub.bu_owner_name} — {sub.bu_owner_email}
      </span>
      <span className="hidden sm:block">
        <Badge className="bg-[#dd260e]/10 text-[#dd260e] border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">
          {sub.outlets_estimate} outlets
        </Badge>
      </span>
      <ChevronDown className={`w-4 h-4 text-[#555555] transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && (
      <div className="px-5 pb-5" data-testid={`submission-detail-${index}`}>
        <SubmissionDetail sub={sub} />
      </div>
    )}
  </div>
);

const DeliveryRow = ({ d, expanded, onToggle, index }) => (
  <div className="border-b border-[#e8e8e8] last:border-0" data-testid={`delivery-row-${index}`}>
    <button
      onClick={onToggle}
      className="w-full grid grid-cols-[1fr_auto] sm:grid-cols-[160px_1fr_1fr_120px_24px] gap-3 px-5 py-4 text-left hover:bg-[#fafafa] transition-colors items-center"
      data-testid={`delivery-toggle-${index}`}
    >
      <span className="text-xs text-[#555555]">{fmtDate(d.created_at)}</span>
      <span className="text-sm font-bold text-[#000000] hidden sm:block">{d.country}</span>
      <span className="text-xs text-[#555555] hidden sm:block truncate">
        {d.contact_name} — {d.contact_email}
      </span>
      <span className="hidden sm:block">
        <Badge className="bg-[#000000]/5 text-[#000000] border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">
          {(d.asset_files || []).length} file{(d.asset_files || []).length === 1 ? "" : "s"}
        </Badge>
      </span>
      <ChevronDown className={`w-4 h-4 text-[#555555] transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
    {expanded && (
      <div className="px-5 pb-5" data-testid={`delivery-detail-${index}`}>
        <DeliveryDetail d={d} />
      </div>
    )}
  </div>
);

const MarketTimeline = ({ market, expandedId, onToggle, index }) => (
  <div className="bg-white border border-[#e8e8e8] rounded-sm" data-testid={`market-card-${index}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-[#e8e8e8]">
      <div className="flex items-center gap-3">
        <span className="font-condensed text-xl font-black uppercase tracking-tight text-[#000000]">{market.country}</span>
        <Badge className="bg-[#dd260e]/10 text-[#dd260e] border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">
          {market.setupCount} setup{market.setupCount === 1 ? "" : "s"}
        </Badge>
        <Badge className="bg-[#000000]/5 text-[#000000] border-transparent rounded-sm text-[10px] font-semibold hover:opacity-100">
          {market.deliveryCount} deliver{market.deliveryCount === 1 ? "y" : "ies"}
        </Badge>
      </div>
      <span className="text-[11px] text-[#999999]">Last activity: {fmtDate(market.lastActivity)}</span>
    </div>

    <div className="px-5 py-5">
      <div className="border-l-2 border-[#e8e8e8] ml-[7px] space-y-1">
        {market.events.map((ev, i) => {
          const isSetup = ev.type === "setup";
          const id = ev.data.id;
          const expanded = expandedId === id;
          return (
            <div key={id} className="relative pl-6 pb-2 last:pb-0" data-testid={`timeline-event-${index}-${i}`}>
              <span
                className={`absolute -left-[9px] top-2 w-4 h-4 rounded-full border-2 ${
                  isSetup ? "bg-[#dd260e] border-[#dd260e]" : "bg-white border-[#000000]"
                }`}
              />
              <button
                onClick={() => onToggle(id)}
                className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-left group"
                data-testid={`timeline-toggle-${index}-${i}`}
              >
                <span
                  className={`text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-sm ${
                    isSetup ? "bg-[#dd260e] text-white" : "bg-[#000000] text-white"
                  }`}
                >
                  {isSetup ? "Country setup" : "Asset delivery"}
                </span>
                <span className="text-xs text-[#999999]">{fmtDate(ev.data.created_at)}</span>
                <span className="text-sm text-[#000000] font-medium">
                  {isSetup
                    ? `${ev.data.bu_owner_name} submitted the full country setup`
                    : `${ev.data.contact_name} delivered ${(ev.data.asset_files || []).length} file${
                        (ev.data.asset_files || []).length === 1 ? "" : "s"
                      }`}
                </span>
                {!isSetup && ev.data.note && (
                  <span className="text-xs text-[#999999] italic truncate max-w-[280px]">"{ev.data.note}"</span>
                )}
                <ChevronDown
                  className={`w-3.5 h-3.5 text-[#999999] group-hover:text-[#000000] transition-transform ${
                    expanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              {expanded && (
                <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-sm p-4 mt-1 mb-2" data-testid={`timeline-detail-${index}-${i}`}>
                  {isSetup ? <SubmissionDetail sub={ev.data} /> : <DeliveryDetail d={ev.data} />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const TabButton = ({ active, onClick, children, testId, withBorder }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
      withBorder ? "border-l border-[#e8e8e8]" : ""
    } ${active ? "bg-[#000000] text-white" : "bg-white text-[#555555] hover:text-[#000000]"}`}
    data-testid={testId}
  >
    {children}
  </button>
);

const EmptyState = ({ title, hint, testId }) => (
  <div className="bg-white border border-[#e8e8e8] rounded-sm p-14 text-center" data-testid={testId}>
    <Inbox className="w-8 h-8 text-[#cccccc] mx-auto mb-3" strokeWidth={1.5} />
    <p className="text-sm font-bold uppercase tracking-wide text-[#000000]">{title}</p>
    <p className="text-xs text-[#999999] mt-1.5">{hint}</p>
  </div>
);

export const Submissions = () => {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("fora_admin_auth") === "1");
  const [subs, setSubs] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [tab, setTab] = useState("markets");
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = () => {
    setError("");
    const get = (path) =>
      fetch(`${API}${path}`).then((r) => {
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        return r.json();
      });
    Promise.all([get("/api/country-onboarding"), get("/api/asset-deliveries")])
      .then(([s, d]) => {
        setSubs(s);
        setDeliveries(d);
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked]);

  const markets = useMemo(() => {
    if (subs === null || deliveries === null) return null;
    const map = {};
    subs.forEach((s) => {
      (map[s.country] = map[s.country] || []).push({ type: "setup", data: s });
    });
    deliveries.forEach((d) => {
      (map[d.country] = map[d.country] || []).push({ type: "delivery", data: d });
    });
    return Object.entries(map)
      .map(([country, events]) => {
        const sorted = events.sort((a, b) => a.data.created_at.localeCompare(b.data.created_at));
        return {
          country,
          events: sorted,
          setupCount: sorted.filter((e) => e.type === "setup").length,
          deliveryCount: sorted.filter((e) => e.type === "delivery").length,
          lastActivity: sorted[sorted.length - 1].data.created_at,
        };
      })
      .sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
  }, [subs, deliveries]);

  const toggle = (id) => setExpandedId(expandedId === id ? null : id);

  return (
    <div data-testid="submissions-view">
      <div className="mb-8">
        <span className="cchbc-eyebrow mb-5">Master F&amp;B — Internal</span>
        <h1
          className="font-condensed text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-4 leading-[0.95]"
          data-testid="submissions-title"
        >
          Country Setup Submissions
        </h1>
        <p className="text-[#555555] text-base max-w-2xl leading-relaxed">
          Everything submitted through the portal — full country setup forms and follow-up asset deliveries, with all
          market details and uploaded files.
        </p>
      </div>

      {!unlocked ? (
        <AdminGate onUnlock={() => setUnlocked(true)} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="inline-flex border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="submissions-tabs">
              <TabButton active={tab === "markets"} onClick={() => { setTab("markets"); setExpandedId(null); }} testId="submissions-tab-markets">
                By market{markets !== null ? ` (${markets.length})` : ""}
              </TabButton>
              <TabButton active={tab === "setups"} onClick={() => { setTab("setups"); setExpandedId(null); }} testId="submissions-tab-setups" withBorder>
                Country setups{subs !== null ? ` (${subs.length})` : ""}
              </TabButton>
              <TabButton active={tab === "deliveries"} onClick={() => { setTab("deliveries"); setExpandedId(null); }} testId="submissions-tab-deliveries" withBorder>
                Asset deliveries{deliveries !== null ? ` (${deliveries.length})` : ""}
              </TabButton>
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#dd260e] hover:underline"
              data-testid="submissions-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {error && (
            <p className="text-sm text-[#dd260e] font-semibold mb-4" data-testid="submissions-error">
              {error}
            </p>
          )}

          {tab === "markets" && markets !== null && markets.length === 0 && (
            <EmptyState title="No activity yet" hint="Country setups and asset deliveries will appear here grouped by market." testId="markets-empty" />
          )}

          {tab === "markets" && markets !== null && markets.length > 0 && (
            <div className="space-y-4" data-testid="markets-list">
              {markets.map((m, i) => (
                <MarketTimeline key={m.country} market={m} index={i} expandedId={expandedId} onToggle={toggle} />
              ))}
            </div>
          )}

          {tab === "setups" && subs !== null && subs.length === 0 && (
            <EmptyState title="No submissions yet" hint="Submitted country setup forms will appear here." testId="submissions-empty" />
          )}

          {tab === "setups" && subs !== null && subs.length > 0 && (
            <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="submissions-table">
              <div className="hidden sm:grid grid-cols-[160px_1fr_1fr_120px_24px] gap-3 bg-[#000000] text-white px-5 py-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider">Submitted</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">Country</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">BU Owner</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">Outlets</span>
                <span />
              </div>
              {subs.map((sub, i) => (
                <SubmissionRow key={sub.id} sub={sub} index={i} expanded={expandedId === sub.id} onToggle={() => toggle(sub.id)} />
              ))}
            </div>
          )}

          {tab === "deliveries" && deliveries !== null && deliveries.length === 0 && (
            <EmptyState title="No asset deliveries yet" hint='Assets sent via "Assets only" mode will appear here.' testId="deliveries-empty" />
          )}

          {tab === "deliveries" && deliveries !== null && deliveries.length > 0 && (
            <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="deliveries-table">
              <div className="hidden sm:grid grid-cols-[160px_1fr_1fr_120px_24px] gap-3 bg-[#000000] text-white px-5 py-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider">Submitted</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">Country</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">Contact</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider">Files</span>
                <span />
              </div>
              {deliveries.map((d, i) => (
                <DeliveryRow key={d.id} d={d} index={i} expanded={expandedId === d.id} onToggle={() => toggle(d.id)} />
              ))}
            </div>
          )}
        </>
      )}

      <PageFooter />
    </div>
  );
};
